export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastOptions {
  duration?: number;
  title?: string;
}

const TOAST_REGION_ID = 'toast-region';
const DEFAULT_DURATION = 4000;

const ICON_PATHS: Record<ToastType, string> = {
  success: 'M5 13l4 4L19 7',
  error: 'M6 18L18 6M6 6l12 12',
  warning: 'M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z',
  info: 'M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z',
};

const DEFAULT_TITLES: Record<ToastType, string> = {
  success: 'Success',
  error: 'Error',
  warning: 'Warning',
  info: 'Note',
};

function ensureRegion(): HTMLElement {
  let region = document.getElementById(TOAST_REGION_ID);
  if (region) return region;

  region = document.createElement('div');
  region.id = TOAST_REGION_ID;
  region.className = 'toast-region';
  region.setAttribute('role', 'region');
  region.setAttribute('aria-label', 'Notifications');
  document.body.appendChild(region);
  return region;
}

function buildIcon(type: ToastType): string {
  const path = ICON_PATHS[type];
  return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${path}"/></svg>`;
}

export function showToast(
  message: string,
  type: ToastType = 'info',
  options: ToastOptions | number = {}
): void {
  const opts: ToastOptions =
    typeof options === 'number' ? { duration: options } : options;
  const duration = opts.duration ?? DEFAULT_DURATION;
  const title = opts.title ?? DEFAULT_TITLES[type];

  const region = ensureRegion();
  const toast = document.createElement('div');
  toast.className = `toast ${type}`;
  toast.setAttribute('role', 'alert');
  toast.setAttribute('aria-live', 'assertive');
  toast.setAttribute('aria-atomic', 'true');

  toast.innerHTML = `
    <span class="toast-icon">${buildIcon(type)}</span>
    <span class="toast-title">${escapeHtml(title)}</span>
    <span class="toast-message">${escapeHtml(message)}</span>
    <button class="toast-close" type="button" aria-label="Dismiss notification">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><path d="M6 18L18 6M6 6l12 12"/></svg>
    </button>
    <span class="toast-progress" style="animation-duration:${duration}ms" aria-hidden="true"></span>
  `;

  region.appendChild(toast);

  let dismissed = false;
  const dismiss = () => {
    if (dismissed) return;
    dismissed = true;
    toast.classList.add('toast-out');
    window.setTimeout(() => toast.remove(), 260);
  };

  const closeBtn = toast.querySelector('.toast-close') as HTMLButtonElement;
  closeBtn.addEventListener('click', dismiss);

  toast.addEventListener('mouseenter', () => {
    const progress = toast.querySelector('.toast-progress') as HTMLElement | null;
    if (progress) progress.style.animationPlayState = 'paused';
  });
  toast.addEventListener('mouseleave', () => {
    const progress = toast.querySelector('.toast-progress') as HTMLElement | null;
    if (progress) progress.style.animationPlayState = 'running';
  });

  window.setTimeout(dismiss, duration);
}

function escapeHtml(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
